import React, { useState } from 'react';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Download, BookOpen, Loader2, Users, UserCog, Sparkles, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function ManualCard({ Icon, role, label, description, downloading, onDownload, testId }) {
  return (
    <div
      className="bg-card border border-border rounded-lg p-6 hover:border-primary/40 transition-colors"
      data-testid={testId}
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Icon size={24} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-lg mb-1">{label}</h3>
          <p className="text-sm text-muted-foreground mb-4">{description}</p>
          <Button
            onClick={() => onDownload(role)}
            disabled={downloading === role}
            data-testid={`download-${role}-btn`}
          >
            {downloading === role ? (
              <Loader2 size={14} className="mr-2 animate-spin" />
            ) : (
              <Download size={14} className="mr-2" />
            )}
            <ManualDownloadLabel role={role} downloading={downloading} />
          </Button>
        </div>
      </div>
    </div>
  );
}

function ManualDownloadLabel({ role, downloading }) {
  const { t } = useI18n();
  return downloading === role ? t('manual.downloading') : t('manual.download');
}

export default function ManualPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(null);

  // Pick the tour icon to match the user's role so they see the journey that applies to them
  const TourIcon = user?.role === 'client' ? Users : user?.role === 'ops' ? UserCog : Sparkles;

  const handleDownload = async (role) => {
    setDownloading(role);
    try {
      const token = localStorage.getItem('ofimex_token');
      const res = await fetch(`${API}/api/manual/${role}.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `OFIMEX_Manual_${role === 'ops' ? 'Operador' : 'Cliente'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('manual.downloadError'));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl space-y-6 p-6">
        <div className="flex items-center gap-3">
          <BookOpen size={28} className="text-primary" />
          <div>
            <h1 className="font-heading text-3xl">{t('manual.title')}</h1>
            <p className="text-muted-foreground mt-1">{t('manual.description')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ManualCard
            Icon={UserCog}
            role="ops"
            label={t('manual.opsLabel')}
            description={t('manual.opsDescription')}
            downloading={downloading}
            onDownload={handleDownload}
            testId="ops-manual-card"
          />
          <ManualCard
            Icon={Users}
            role="client"
            label={t('manual.clientLabel')}
            description={t('manual.clientDescription')}
            downloading={downloading}
            onDownload={handleDownload}
            testId="client-manual-card"
          />
        </div>

        {/* Welcome tour replay */}
        <div
          className="bg-card border border-border rounded-lg p-6 hover:border-primary/40 transition-colors"
          data-testid="welcome-tour-card"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <TourIcon size={24} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-lg mb-1">{t('manual.tourLabel')}</h3>
              <p className="text-sm text-muted-foreground mb-4">{t('manual.tourDescription')}</p>
              <Button
                variant="outline"
                onClick={() => window.dispatchEvent(new CustomEvent('ofimex:open-tour'))}
                data-testid="open-tour-btn"
              >
                <PlayCircle size={14} className="mr-2" />
                {t('manual.tourButton')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
